import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext, useState } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import PasswordTextField from 'shared/lib/components/controls/textFields/passwordTextField';
import { useTranslation } from 'react-i18next';

export default observer(function RecoverForm({
  onSubmit,
}: {
  onSubmit: (data: { password: string }) => Promise<void>;
}) {
  const { t } = useTranslation();

  const {
    mobxStore: { authStore },
  } = useContext(MobxContext);
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object({
    password: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .min(8, t('errors.validationSchema.passwordTooShort')),
    passwordConfirm: yup
      .mixed()
      .required(t('errors.validationSchema.required'))
      .oneOf(
        [yup.ref('password')],
        t('errors.validationSchema.passwordNotConfirmed')
      ),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      password: '',
      passwordConfirm: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (signupData) => {
      try {
        setIsLoading(true);
        await onSubmit({
          ...signupData,
        });
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        console.error(e);
      }
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  return (
    <>
      <Typography variant="f14" sx={{ textAlign: 'center', fontWeight: 300 }}>
        Пожалуйста, после заполнения полей сохраните пароль, чтобы больше
        не&nbsp;терять:)
      </Typography>

      <form
        id="recoverForm"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(sp.s60),
        }}
        onSubmit={formik.handleSubmit}
      >
        <PasswordTextField
          label={t('login.password')}
          id="password"
          name="password"
          placeholder={t('login.passwordPlaceholder')}
          value={formik.values.password}
          onChange={handleOnChange}
          onBlur={formik.handleBlur}
          error={isFormikFieldError('password')}
          helperText={getFormikFieldErrorText('password')}
        ></PasswordTextField>

        <PasswordTextField
          placeholder={t('login.passwordPlaceholder')}
          id="passwordConfirm"
          name="passwordConfirm"
          value={formik.values.passwordConfirm}
          onChange={handleOnChange}
          onBlur={formik.handleBlur}
          error={isFormikFieldError('passwordConfirm')}
          helperText={getFormikFieldErrorText('passwordConfirm')}
        ></PasswordTextField>
      </form>

      <PrimaryButton
        sx={{
          width: 1,
        }}
        form="recoverForm"
        variant="contained"
        type="submit"
        loading={isLoading}
        onClick={() => {
          setFormikSubmited(true);
        }}
      >
        <Typography variant="f20">{t('common.save')}</Typography>
      </PrimaryButton>
    </>
  );
});
